import { afterNextRender, booleanAttribute, computed, Component, input, output, signal } from '@angular/core';
import { PnIcon } from '@privanot/shared-ui/common/icon';
import type { PnIconSize } from '@privanot/shared-ui/common/icon';
import { ButtonModule } from 'primeng/button';

export type PnButtonSize = 'small' | 'large' | undefined;
export type PnButtonSeverity = 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'help' | 'contrast';
export type PnButtonVariant = 'text' | 'outlined' | 'ghost' | 'link';

@Component({
    selector: 'pn-button',
    standalone: true,
    imports: [ButtonModule, PnIcon],
    templateUrl: './button.html',
    styleUrl: './button.scss',
})
export class PnButton {
    /** Display Text */
    text = input<string>();

    /** Severity ('success' | 'info' | 'warn' | 'danger' | 'help' | 'primary' | 'secondary' | 'contrast'). Default: 'primary' */
    severity = input<PnButtonSeverity>('primary');

    /** Button type. Default: 'button' */
    type = input<'button' | 'submit' | 'reset'>('button');

    /** If button should be disabled. If clicked, it will be automatically disabled until asyncAction is completed. */
    disabled = input<boolean>();

    /** If button should be in a "loading" state. If clicked, it will be automatically set to a loading state until asyncAction is completed. */
    loading = input<boolean>(false);

    /** Button click action. Automatically disables the button and sets a loading state on it until the action completes. */
    asyncAction = input<((event: MouseEvent) => Promise<void>) | undefined>(undefined);

    /** Event sent when button is clicked. Will fire regardless of asyncAction state. */
    clicked = output<MouseEvent>();

    /** Whether the input should stretch to fill its container */
    fluid = input(false, { transform: booleanAttribute });

    /** Whether the button is rounded */
    rounded = input(false, { transform: booleanAttribute });

    /** Button variant */
    variant = input<PnButtonVariant>();

    /** Lucide icon to add to the button */
    icon = input<string>();

    /** Button size. Default: undefined (medium). PrimeNG handles padding/height/font-size. */
    size = input<PnButtonSize>();

    /** Icon size. Defaults to match the button size (s for small, l for large, m otherwise). */
    iconSize = input<PnIconSize | number>();

    protected resolvedIconSize = computed((): PnIconSize | number => {
        const explicit = this.iconSize();
        if (explicit !== undefined) return explicit;
        const size = this.size();
        if (size === 'small') return 's';
        if (size === 'large') return 'l';
        return 'm';
    });

    /** Icon position relative to the text. Default: 'left' */
    iconPos = input<'left' | 'right' | 'top' | 'bottom'>('left');

    protected actionPending = signal<boolean>(false);

    protected isLoading = computed(() => this.loading() || this.actionPending());

    // TODO Fix for flashing buttons - paired with CSS fix in theme.ts, see comments there for more details.
    // This makes the button have no transition on initial load, then sets all of its transitions after it's "settled", meaning all styles have been loaded.
    protected settled = signal(false);
    constructor() {
        afterNextRender(() => {
            requestAnimationFrame(() => this.settled.set(true));
        });
    }

    protected async handleClick(event: MouseEvent) {
        this.clicked.emit(event);

        const action = this.asyncAction();
        if (action) {
            this.actionPending.set(true);
            try {
                await action(event);
            } finally {
                this.actionPending.set(false);
            }
        }
    }
}
